import { useState, useEffect } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'

function App() {
  const [shopProducts, setShopProducts] = useState<Array<{title:string; price:string; image:string; handle:string}>>([])
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const fetchConfigAndProducts = async () => {
      try {
        const resp = await fetch('https://webhub-6b3.pages.dev/api/shop-config')
        const data = await resp.json()
        const configs = data.configs || {}
        let featuredHandles: string[] | null = null
        const hostname = location.hostname
        for (const [key, cfg] of Object.entries(configs)) {
          if (Array.isArray(cfg)) {
            if (hostname.includes(key) || key.includes(hostname)) {
              featuredHandles = cfg
              break
            }
          } else if (typeof cfg === 'object' && cfg !== null && 'productHandles' in cfg) {
            const c = cfg as { productHandles: string[]; theme?: string; layout?: string }
            if (hostname.includes(key) || key.includes(hostname)) {
              featuredHandles = c.productHandles
              break
            }
          }
        }
        if (!featuredHandles || featuredHandles.length === 0) {
          featuredHandles = ['tai-chi-glass-wide-mouth-pot', 'tai-chi-teapot', 'tai-chi-tiger', 'tai-chi-fish-shaped-ceramic-plate']
        }
        const products = await Promise.all(
          featuredHandles.map(handle =>
            fetch(`https://lemaharmony.com/products/${handle}.json`)
              .then(res => res.json())
              .then(d => ({ title: d.product.title, price: d.product.variants[0]?.price || '0', image: d.product.images[0]?.src || '', handle: d.product.handle }))
              .catch(() => null)
          )
        )
        setShopProducts(products.filter(Boolean) as any[])
      } catch {
        const fallbackHandles = ['tai-chi-glass-wide-mouth-pot', 'tai-chi-teapot', 'tai-chi-tiger', 'tai-chi-fish-shaped-ceramic-plate']
        const products = await Promise.all(
          fallbackHandles.map(handle =>
            fetch(`https://lemaharmony.com/products/${handle}.json`)
              .then(res => res.json())
              .then(d => ({ title: d.product.title, price: d.product.variants[0]?.price || '0', image: d.product.images[0]?.src || '', handle: d.product.handle }))
              .catch(() => null)
          )
        )
        setShopProducts(products.filter(Boolean) as any[])
      }
    }
    fetchConfigAndProducts()
  }, [])

  const destinations = [
    { id: 'wuhan', name: 'Wuhan', subtitle: 'Capital of Hubei · River City', emoji: '🏙️', description: 'A modern metropolis where the Yangtze and Han Rivers meet. Home to Yellow Crane Tower, East Lake, and vibrant street food.', color: 'from-blue-500 to-blue-700' },
    { id: 'yichang', name: 'Yichang', subtitle: 'Gateway to the Three Gorges', emoji: '🌊', description: 'Location of the Three Gorges Dam, the grand starting point of the Yangtze River Gorges, with picturesque Qingjiang Gallery.', color: 'from-cyan-500 to-cyan-700' },
    { id: 'shiyan', name: 'Shiyan', subtitle: 'Foot of Wudang Mountain', emoji: '🏔️', description: 'Home to Wudang Mountain — a Taoist sacred site, cradle of Tai Chi, and UNESCO World Heritage destination.', color: 'from-emerald-500 to-emerald-700' },
    { id: 'xiangyang', name: 'Xiangyang', subtitle: 'Ancient Three Kingdoms City', emoji: '🏯', description: 'A historic city with 2,800 years of heritage, famous stage of the Three Kingdoms, and the hometown of Zhuge Liang.', color: 'from-amber-500 to-amber-700' },
    { id: 'enshi', name: 'Enshi', subtitle: 'Tujia & Miao Culture', emoji: '🌲', description: 'Tujia and Miao autonomous prefecture featuring the Grand Canyon, Tenglong Cave, and stunning ethnic minority culture.', color: 'from-green-600 to-green-800' },
    { id: 'shennongjia', name: 'Shennongjia', subtitle: 'Primeval Wilderness', emoji: '🌿', description: 'A UNESCO World Natural Heritage site — the roof of Central China, with ancient forests, golden monkeys, and wild man legends.', color: 'from-teal-600 to-teal-800' }
  ]

  const activities = [
    { emoji: '🚢', title: 'Three Gorges Cruise', description: 'Sail through the breathtaking Yangtze River Gorges on a luxury cruise, experiencing centuries of poetry and legend.' },
    { emoji: '⛰️', title: 'Wudang Pilgrimage', description: 'Ascend Golden Summit, practice Tai Chi, sip Taoist tea, and immerse yourself in profound spiritual heritage.' },
    { emoji: '🎭', title: 'Tujia Folk Culture', description: 'Join the Tujia摆手舞 (Baishou Dance), experience local festivals, and admire exquisite Xilan Kapu brocade.' },
    { emoji: '🍜', title: 'Breakfast Culture Tour', description: 'Hot dry noodles, tofu skin, mianwo, glutinous rice rolls — Wuhan morning market is a culinary adventure.' },
    { emoji: '🏯', title: 'Yellow Crane Tower', description: 'Climb this ancient poetic tower for panoramic views of the Yangtze River Bridge and the city of Wuhan.' },
    { emoji: '🌲', title: 'Shennongjia Expedition', description: 'Hike through primeval forests, spot golden snub-nosed monkeys, and explore the mysterious wild man legends.' }
  ]

  const travelTips = [
    { icon: '📅', title: 'Best Time to Visit', description: 'Spring (Mar-May) and autumn (Sep-Nov) offer the most pleasant weather. See autumn colors at Wudang, spring waters at the Gorges.' },
    { icon: '🚄', title: 'Getting Around', description: 'Wuhan is a national high-speed rail hub — 4-5 hours to Beijing, Shanghai, Guangzhou, and Shenzhen. Provincial intercity rail covers all cities.' },
    { icon: '🍲', title: 'Must-Try Local Food', description: 'Hot dry noodles (热干面), Wuchang fish, lotus root pork rib soup, duck neck, tofu skin, and three-fresh tofu skin.' },
    { icon: '🤝', title: 'Cultural Etiquette', description: 'Dress modestly when visiting temples and monasteries. Wuhan locals are warm and direct — a simple greeting goes a long way.' },
    { icon: '📋', title: 'Required Documents', description: 'International visitors need a valid passport and visa. Domestic travelers should carry their ID card at all times.' },
    { icon: '🎫', title: 'Ticket Discounts', description: 'Many attractions offer discounts for seniors (60+), students, and military personnel. Email to apply for free tickets.' }
  ]

  const sisterSites = [
    { name: 'HelloYichang', url: 'https://helloyichang.com', emoji: '🌊', description: 'Yichang Travel Guide' },
    { name: 'WudangHub', url: 'https://wudanghub.com', emoji: '🏔️', description: 'Wudang Culture Portal' },
    { name: 'HelloWuchang', url: 'https://hellowuchang.com', emoji: '🏙️', description: 'Wuchang Cultural Guide' },
    { name: 'LEMA Harmony', url: 'https://lemaharmony.com', emoji: '🛒', description: 'Cultural Souvenir Shop' }
  ]

  return (
    <>
      <SEO 
        title="HiHubei - Discover the Beauty of Hubei"
        description="Your authoritative guide to Hubei tourism — explore the Three Gorges, Wudang Mountain, Yellow Crane Tower, Shennongjia, and more. Travel tips, free tickets, latest news."
        keywords="Hubei, travel, tourism, Three Gorges, Wudang Mountain, Yellow Crane Tower, Shennongjia, Wuhan, Yichang, Enshi, Hubei attractions, Hubei food, Hubei guide"
        type="website"
      />
      <div className="min-h-screen bg-white text-gray-900 font-sans">

        {/* ========== Navigation ========== */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-20">
              <a href="#" className="flex items-center space-x-3 group">
                <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-amber-500 to-red-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                  <span className="text-white text-sm md:text-lg font-bold">HB</span>
                </div>
                <div>
                  <h1 className="text-lg md:text-xl font-bold" style={{ color: scrolled ? '#111827' : '#fff' }}>HiHubei</h1>
                  <p className="text-xs hidden sm:block" style={{ color: scrolled ? '#6b7280' : 'rgba(255,255,255,0.6)' }}>Hubei Travel & Culture</p>
                </div>
              </a>

              <nav className="hidden md:flex items-center space-x-1">
                {['Destinations', 'Activities', 'Articles', 'Shop', 'Contact'].map((item, i) => {
                  const ids = ['destinations', 'activities', 'articles', 'shop', 'contact']
                  return (
                    <a key={ids[i]} href={`#${ids[i]}`} className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-white/10" style={{ color: scrolled ? '#374151' : 'rgba(255,255,255,0.85)' }}
                      onMouseOver={e => { e.currentTarget.style.backgroundColor = scrolled ? '#fffbeb' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = scrolled ? '#d97706' : '#fff' }}
                      onMouseOut={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = scrolled ? '#374151' : 'rgba(255,255,255,0.85)' }}>
                      {item}
                    </a>
                  )
                })}
              </nav>

              <div className="flex items-center space-x-3">
                <a href="mailto:info@hihubei.com" className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all border"
                  style={{ color: scrolled ? '#374151' : 'rgba(255,255,255,0.85)', backgroundColor: scrolled ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.1)', borderColor: scrolled ? '#e5e7eb' : 'rgba(255,255,255,0.2)' }}>
                  📧 Contact Us
                </a>
                <a href="#destinations" className="px-4 py-2 md:px-5 md:py-2.5 bg-gradient-to-r from-amber-500 to-red-600 text-white text-sm font-bold rounded-lg hover:from-amber-600 hover:to-red-700 transition-all shadow-md">Start Exploring</a>
              </div>

              <details className="md:hidden relative">
                <summary className="list-none p-2 rounded-lg cursor-pointer" style={{ color: scrolled ? '#111827' : '#fff' }}>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </summary>
                <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2">
                  {['Destinations', 'Activities', 'Articles', 'Shop', 'Contact'].map((item, i) => {
                    const ids = ['destinations', 'activities', 'articles', 'shop', 'contact']
                    return <a key={item} href={`#${ids[i]}`} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700">{item}</a>
                  })}
                </div>
              </details>
            </div>
          </div>
        </header>

        {/* ========== Hero ========== */}
        <section className="relative min-h-screen flex items-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900 via-red-900 to-stone-900"></div>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 80 Q25 60 50 75 Q75 90 100 70 L100 100 L0 100Z' fill='%23ffffff' opacity='0.3'/%3E%3C/svg%3E")`, backgroundSize: '200px 200px' }}></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/10">
                <span className="text-amber-300">🏯</span>
                <span className="text-sm font-medium text-white/90">Welcome to Hubei</span>
              </div>
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight">
                Discover Hubei<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400">Explore 2,500 Years of Chu Culture</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/80 mb-10 max-w-2xl leading-relaxed">
                From the majestic Three Gorges and the sacred peaks of Wudang Mountain to the vibrant streets of Wuhan and the untouched wilderness of Enshi —<br className="hidden sm:block" />
                Hubei, the heart of ancient Chu civilization, awaits your discovery.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#destinations" className="inline-flex items-center px-6 py-3.5 bg-gradient-to-r from-amber-500 to-red-600 text-white font-bold rounded-xl hover:from-amber-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl gap-2">
                  Start Exploring
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                </a>
                <a href="mailto:info@hihubei.com" className="inline-flex items-center px-6 py-3.5 bg-white/10 backdrop-blur-sm text-white font-medium rounded-xl hover:bg-white/20 transition-all border border-white/20 gap-2">🎫 Get Free Tickets</a>
              </div>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
              {[
                { num: '5', label: 'UNESCO World Heritage Sites', icon: '🏛️' },
                { num: '12', label: '5A-Rated Scenic Spots', icon: '⭐' },
                { num: '80+', label: 'Free-Attraction Destinations', icon: '🎫' },
                { num: '2,500+', label: 'Years of Chu Civilization', icon: '📜' }
              ].map(stat => (
                <div key={stat.label} className="text-center p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/5">
                  <div className="text-2xl mb-1">{stat.icon}</div>
                  <div className="text-2xl md:text-3xl font-bold text-amber-300">{stat.num}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== Destinations ========== */}
        <section id="destinations" className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-block text-sm font-semibold text-amber-600 bg-amber-50 px-4 py-1.5 rounded-full mb-4">🌍 Explore Destinations</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">Top Destinations in Hubei</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">From modern cities to natural wonders, ancient history to ethnic culture — every side of Hubei is worth exploring.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {destinations.map(dest => (
                <a key={dest.id} href="#" className="group relative overflow-hidden rounded-2xl bg-white shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  <div className={`h-32 bg-gradient-to-br ${dest.color} flex items-center justify-center relative overflow-hidden`}>
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}></div>
                    <span className="text-5xl relative z-10">{dest.emoji}</span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-baseline gap-2 mb-1">
                      <h3 className="text-xl font-bold text-gray-900">{dest.name}</h3>
                      <span className="text-xs text-gray-400">{dest.subtitle}</span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{dest.description}</p>
                    <div className="mt-4 flex items-center text-amber-600 text-sm font-medium group-hover:gap-2 transition-all">
                      Learn More
                      <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ========== Featured Activities ========== */}
        <section id="activities" className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-block text-sm font-semibold text-red-600 bg-red-50 px-4 py-1.5 rounded-full mb-4">🎯 Featured Activities</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">Unmissable Hubei Experiences</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">Every experience is a memory only Hubei can give you.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {activities.map(act => (
                <div key={act.title} className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-amber-100 hover:border-amber-300 hover:-translate-y-1">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <span className="text-2xl">{act.emoji}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{act.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{act.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== Free Tickets ========== */}
        <section className="py-20 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M0 0h40v40H0z' fill='none'/%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/svg%3E")` }}></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full mb-6 shadow-lg">
              <span className="text-white text-2xl">🎫</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Get <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-400">Free Tickets</span> to Hubei Attractions
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">Up to 5 free tickets per person — just send us an email to apply</p>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 inline-block">
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                <div className="text-4xl">📧</div>
                <div className="text-left">
                  <p className="text-white/70 text-sm mb-1">Email us to claim your free tickets</p>
                  <a href="mailto:info@hihubei.com" className="text-xl sm:text-2xl font-bold text-amber-300 hover:text-amber-200 transition-colors">info@hihubei.com</a>
                </div>
                <a href="mailto:info@hihubei.com" className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-bold rounded-xl hover:from-amber-600 hover:to-yellow-700 transition-all shadow-lg whitespace-nowrap">Apply Now →</a>
              </div>
            </div>
            <p className="text-white/50 text-sm mt-6">⏰ Limited tickets available, first come first served. Valid for Hubei attractions only.</p>
          </div>
        </section>

        {/* ========== Travel News ========== */}
        <NewsSection website="hihubei" limit={10000} title="Hubei Travel News" themeColor="red" />

        {/* ========== LEMA Shop ========== */}
        <section id="shop" className="py-20 bg-gradient-to-br from-stone-50 to-amber-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-10 gap-4">
              <div>
                <span className="inline-block text-sm font-semibold text-amber-600 bg-amber-50 px-4 py-1.5 rounded-full mb-3">🛒 Cultural Souvenirs</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Hubei Collection · LEMA Harmony</h2>
                <p className="text-gray-600 mt-2">Crafted with heart — bring the spirit of Hubei home</p>
              </div>
              <a href="https://lemaharmony.com" target="_blank" rel="noopener noreferrer" className="px-5 py-3 bg-gradient-to-r from-amber-500 to-red-600 text-white font-bold rounded-xl hover:from-amber-600 hover:to-red-700 transition-all shadow-md whitespace-nowrap">Browse All →</a>
            </div>
            {shopProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {shopProducts.map((product, idx) => (
                  <a key={idx} href={`https://lemaharmony.com/products/${product.handle}`} target="_blank" rel="noopener noreferrer" className="group bg-white rounded-2xl shadow-md border border-stone-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="aspect-square bg-stone-50 overflow-hidden">
                      <img src={product.image} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{product.title}</h3>
                      <p className="text-amber-700 font-bold mt-1">${product.price}</p>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="bg-white/80 rounded-2xl p-12 text-center border border-dashed border-amber-200">
                <div className="text-5xl mb-4">🛒</div>
                <p className="text-gray-700 font-medium text-lg mb-3">Discover Artisan Creations</p>
                <p className="text-gray-500 text-sm mb-6">Explore exquisite crafts inspired by Tai Chi, tea culture, and Chinese heritage.</p>
                <a href="https://lemaharmony.com" target="_blank" rel="noopener noreferrer" className="inline-block px-6 py-3 bg-gradient-to-r from-amber-500 to-red-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-red-700 transition-all">Visit LEMA Harmony →</a>
              </div>
            )}
          </div>
        </section>

        {/* ========== Travel Tips ========== */}
        <section id="articles" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-block text-sm font-semibold text-emerald-600 bg-emerald-50 px-4 py-1.5 rounded-full mb-4">📖 Travel Tips</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">Essential Hubei Travel Tips</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">Good preparation makes for a smoother journey through Hubei</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {travelTips.map((tip, i) => (
                <div key={i} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-md border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-2xl">{tip.icon}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{tip.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{tip.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== Footer ========== */}
        <footer id="contact" className="py-20 bg-gray-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-red-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-lg font-bold">HB</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">HiHubei</h3>
                    <p className="text-gray-400 text-sm">Hubei Travel & Culture — Discover 2,500 Years of Heritage</p>
                  </div>
                </div>
                <p className="text-gray-400 max-w-xl mb-8 leading-relaxed">
                  We are dedicated to providing global travelers with the most comprehensive guide to Hubei.<br />
                  From the majestic Three Gorges to the sacred peaks of Wudang — explore the real Hubei.
                </p>
                <div className="inline-flex items-center space-x-4 bg-white/5 rounded-2xl px-6 py-4 border border-white/10">
                  <span className="text-3xl">📧</span>
                  <div>
                    <p className="text-gray-400 text-sm">Email us for inquiries</p>
                    <a href="mailto:info@hihubei.com" className="text-lg font-semibold text-amber-300 hover:text-amber-200 transition-colors">info@hihubei.com</a>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-6">More Amazing Sites</h4>
                <ul className="space-y-4">
                  {sisterSites.map(site => (
                    <li key={site.name}>
                      <a href={site.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group hover:bg-white/5 rounded-xl p-3 -m-3 transition-colors">
                        <span className="text-2xl">{site.emoji}</span>
                        <div>
                          <p className="font-semibold group-hover:text-amber-300 transition-colors">{site.name}</p>
                          <p className="text-sm text-gray-400">{site.description}</p>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} HiHubei. All rights reserved. | Technical Support @ <a href="https://ninehub.cn" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors">Nine Hub</a></p>
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <a href="mailto:info@hihubei.com" className="hover:text-amber-300 transition-colors">Contact</a>
                <a href="https://hihubei.com/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors">Sitemap</a>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  )
}

export default App